// Alerts page. Every alarm the system has raised, in the current scope:
//   - Real alarms  (status === "confirmed") — action was warranted
//   - False alarms (status === "cleared")   — reviewed, no action
// Anything still "pending" verification sits in a strip on top.
//
// One card design throughout (see weaponAlertsShared.ALERT_CARD): ink-soft
// panel, brand hairline, severity on the left accent and badge.

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { AlertTriangle, Filter, ShieldAlert, ShieldCheck, X } from "lucide-react";
import { readAlertDisplayPrefs, showOnAlertsPage } from "../alertDisplayPrefs";
import { ALERTS_PAGE_SIZE, api, WeaponAlert } from "../api";
import { PageHeader } from "../components/PageHeader";
import { useRole } from "../hooks/useRole";
import { useScope } from "../components/ScopeContext";
import { useWeaponAlertsContext } from "../components/WeaponAlertsContext";
import { MultiSelect } from "../components/MultiSelect";
import PolicyAlerts from "./PolicyAlerts";
import {
  ACTION_TEXT,
  ALERT_CARD,
  ALERT_CARD_HAIRLINE,
  ALERTS_INITIAL_VISIBLE,
  AlertCropImage,
  AlertSummary,
  alertStatusStyle,
  isPolicyAlert,
  policyName,
  ribbonPremiumCopy,
} from "../components/weaponAlertsShared";

// A stable "policy" grouping key for filtering: named policies group by
// their name; weapon detections all group under "Weapon".
function policyKey(a: WeaponAlert): string {
  if (isPolicyAlert(a)) return policyName(a);
  return "Weapon";
}

function mergeAlerts(...lists: WeaponAlert[][]): WeaponAlert[] {
  const byId = new Map<string, WeaponAlert>();
  for (const list of lists) {
    for (const a of list) {
      const prev = byId.get(a.alert_id);
      if (!prev || a.updated_at_ms >= prev.updated_at_ms) byId.set(a.alert_id, a);
    }
  }
  return [...byId.values()].sort((a, b) => b.updated_at_ms - a.updated_at_ms);
}

function timeLabel(ms: number): string {
  return new Date(ms).toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

function AlertRow({
  alert,
  cameraName,
  storeName,
  variant,
  onEnlarge,
  onDismiss,
}: {
  alert: WeaponAlert;
  cameraName: string;
  storeName: string;
  variant: "pending" | "review";
  onEnlarge: (a: WeaponAlert) => void;
  onDismiss: (a: WeaponAlert) => void;
}) {
  const style = alertStatusStyle(variant, alert);
  const copy = ribbonPremiumCopy(alert, cameraName, storeName);

  return (
    <div className={`group p-2.5 pr-6 ${ALERT_CARD}`}>
      <span className={ALERT_CARD_HAIRLINE} aria-hidden />
      <span className={`absolute inset-y-0 left-0 w-1 ${style.bar}`} aria-hidden />

      <div className="flex items-stretch gap-3">
        <AlertCropImage
          matchHeight
          alertId={alert.alert_id}
          alt={`Alert ${alert.alert_id}`}
          buttonClassName={`ml-1 shrink-0 self-stretch overflow-hidden rounded-lg ${style.thumbBorder} focus:outline-none focus:ring-2 focus:ring-bf-violet/60`}
          onClick={() => onEnlarge(alert)}
        />

        <div className="min-w-0 flex-1 py-0.5">
          <AlertSummary
            label={copy.label}
            title={copy.title}
            message={copy.message}
            store={copy.store}
            camera={copy.camera}
            time={timeLabel(alert.updated_at_ms)}
            pillClass={style.pill}
            icon={<AlertTriangle size={20} className="shrink-0" />}
          />
        </div>
      </div>

      <button
        type="button"
        onClick={() => onDismiss(alert)}
        className="absolute right-2 top-2 z-10 rounded-md bg-ink-soft/90 p-1 text-bf-muted opacity-0 transition hover:bg-brand-purple/20 hover:text-white focus:opacity-100 group-hover:opacity-100"
        aria-label="Remove alert"
      >
        <X size={15} />
      </button>
    </div>
  );
}

function PendingAlertGrid({
  items,
  totalCount,
  hasMore,
  onNeedMore,
  cameraNameById,
  storeNameFor,
  onEnlarge,
  onDismiss,
}: {
  items: WeaponAlert[];
  totalCount?: number;
  hasMore?: boolean;
  onNeedMore?: () => Promise<void>;
  cameraNameById: Map<string, string>;
  storeNameFor: (a: WeaponAlert) => string;
  onEnlarge: (a: WeaponAlert) => void;
  onDismiss: (a: WeaponAlert) => void;
}) {
  const [visible, setVisible] = useState(ALERTS_INITIAL_VISIBLE);
  const shown = items.slice(0, visible);
  const count = totalCount ?? items.length;
  const remaining = Math.max(0, count - shown.length);
  const canShowMore = remaining > 0 || !!hasMore;

  return (
    <>
      <div className="grid gap-2 sm:grid-cols-2">
        {shown.map((a) => (
          <AlertRow
            key={a.alert_id}
            alert={a}
            cameraName={cameraNameById.get(a.camera_id) ?? a.camera_id}
            storeName={storeNameFor(a)}
            variant="pending"
            onEnlarge={onEnlarge}
            onDismiss={onDismiss}
          />
        ))}
      </div>
      {canShowMore && (
        <button
          type="button"
          onClick={() => {
            void (async () => {
              if (visible >= items.length && hasMore && onNeedMore) await onNeedMore();
              setVisible((n) => n + ALERTS_INITIAL_VISIBLE);
            })();
          }}
          className={`${ACTION_TEXT} mt-2 w-full py-1.5 text-center`}
        >
          Show more ({remaining > 0 ? remaining : "more"})
        </button>
      )}
    </>
  );
}

function AlertColumn({
  title,
  icon,
  accentClass,
  items,
  totalCount,
  hasMore,
  onNeedMore,
  cameraNameById,
  storeNameFor,
  emptyText,
  onEnlarge,
  onDismiss,
}: {
  title: string;
  icon: ReactNode;
  accentClass: string;
  items: WeaponAlert[];
  totalCount?: number;
  hasMore?: boolean;
  onNeedMore?: () => Promise<void>;
  cameraNameById: Map<string, string>;
  storeNameFor: (a: WeaponAlert) => string;
  emptyText: string;
  onEnlarge: (a: WeaponAlert) => void;
  onDismiss: (a: WeaponAlert) => void;
}) {
  const [visible, setVisible] = useState(ALERTS_INITIAL_VISIBLE);
  const shown = items.slice(0, visible);
  const count = totalCount ?? items.length;
  const remaining = Math.max(0, count - shown.length);
  const canShowMore = remaining > 0 || !!hasMore;

  return (
    <section className="flex min-h-0 flex-col">
      <div className="mb-3 flex items-center gap-2">
        <span className={accentClass}>{icon}</span>
        <h2 className="text-base font-semibold text-white">{title}</h2>
        <span className="rounded-full bg-brand-purple/15 px-2 py-0.5 text-xs font-medium text-violet-200 ring-1 ring-inset ring-brand-violet/25">
          {count}
        </span>
      </div>
      {items.length === 0 && !hasMore ? (
        <p className="flex flex-1 items-center justify-center rounded-2xl border border-dashed border-brand-violet/25 py-10 text-center text-sm text-bf-muted">
          {emptyText}
        </p>
      ) : (
        <div className="space-y-2 overflow-y-auto pr-1">
          {shown.map((a) => (
            <AlertRow
              key={a.alert_id}
              alert={a}
              cameraName={cameraNameById.get(a.camera_id) ?? a.camera_id}
              storeName={storeNameFor(a)}
              variant="review"
              onEnlarge={onEnlarge}
              onDismiss={onDismiss}
            />
          ))}
          {canShowMore && (
            <button
              type="button"
              onClick={() => {
                void (async () => {
                  if (visible >= items.length && hasMore && onNeedMore) await onNeedMore();
                  setVisible((n) => n + ALERTS_INITIAL_VISIBLE);
                })();
              }}
              className={`${ACTION_TEXT} w-full py-1.5 text-center`}
            >
              Show more ({remaining > 0 ? remaining : "more"})
            </button>
          )}
        </div>
      )}
    </section>
  );
}

export default function Alerts() {
  const {
    pending,
    reviewItems,
    dismissOne,
    setEnlarged,
    displayPrefs,
    pendingTotal,
    confirmedTotal,
    historyHasMore,
  } = useWeaponAlertsContext();
  const { scope, scopeLabel, stores, scopeKey, scopeParams } = useScope();
  const role = useRole();
  const nav = useNavigate();
  const search = useSearch({ strict: false }) as { tab?: string };
  const tab = role.canManage && search.tab === "policies" ? "policies" : "alerts";

  useEffect(() => {
    if (search.tab === "policies" && !role.isLoading && !role.canManage) {
      void nav({ to: "/alerts", search: { tab: undefined }, replace: true });
    }
  }, [search.tab, role.canManage, role.isLoading, nav]);

  // Names for all cameras, not the scoped subset: an alarm's camera should
  // still be named even if the scope moved on since it was raised.
  const cameras = useQuery({
    queryKey: ["cameras", "all"],
    queryFn: () => api.cameras.list(),
  });
  const cameraNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of cameras.data ?? []) map.set(c.id, c.name?.trim() || c.id);
    return map;
  }, [cameras.data]);
  const storeNameFor = useMemo(() => {
    const display = new Map<string, string>();
    for (const s of stores) display.set(s.name, s.display_name || s.name);
    const byCam = new Map<string, string>();
    for (const c of cameras.data ?? []) {
      if (c.store) byCam.set(c.id, c.store);
    }
    return (a: WeaponAlert) => {
      const key = a.store || byCam.get(a.camera_id) || "";
      return display.get(key) || key;
    };
  }, [stores, cameras.data]);

  const [selectedPolicies, setSelectedPolicies] = useState<string[]>([]);
  const [selectedCameras, setSelectedCameras] = useState<string[]>([]);
  const [hiddenIds, setHiddenIds] = useState<Set<string>>(() => new Set());
  const [morePending, setMorePending] = useState<WeaponAlert[]>([]);
  const [moreConfirmed, setMoreConfirmed] = useState<WeaponAlert[]>([]);
  const [moreHistory, setMoreHistory] = useState<WeaponAlert[]>([]);
  const [historyMore, setHistoryMore] = useState(false);
  const [historyOffset, setHistoryOffset] = useState(ALERTS_PAGE_SIZE);
  const loadingMore = useRef(false);

  useEffect(() => {
    setMorePending([]);
    setMoreConfirmed([]);
    setMoreHistory([]);
    setHistoryOffset(ALERTS_PAGE_SIZE);
    setHistoryMore(historyHasMore);
  }, [scopeKey]);

  useEffect(() => {
    if (moreHistory.length === 0) setHistoryMore(historyHasMore);
  }, [historyHasMore, moreHistory.length]);

  const pendingItems = useMemo(() => mergeAlerts(pending, morePending), [pending, morePending]);
  const reviewMerged = useMemo(
    () => mergeAlerts(reviewItems, moreConfirmed, moreHistory),
    [reviewItems, moreConfirmed, moreHistory],
  );

  const loadMore = useCallback(
    async (kind: "pending" | "confirmed" | "history") => {
      if (loadingMore.current) return;
      loadingMore.current = true;
      try {
        const page = await api.alerts.weapon(scopeParams, {
          limit: ALERTS_PAGE_SIZE,
          pendingOffset: kind === "pending" ? pending.length + morePending.length : 0,
          confirmedOffset:
            kind === "confirmed"
              ? reviewItems.filter((a) => a.status === "confirmed").length +
                moreConfirmed.length
              : 0,
          historyOffset: kind === "history" ? historyOffset : 0,
        });
        if (kind === "pending") setMorePending((prev) => mergeAlerts(prev, page.pending));
        if (kind === "confirmed") setMoreConfirmed((prev) => mergeAlerts(prev, page.confirmed));
        if (kind === "history") {
          setMoreHistory((prev) => mergeAlerts(prev, page.history));
          setHistoryOffset((n) => n + ALERTS_PAGE_SIZE);
          setHistoryMore(page.history_has_more);
        }
      } finally {
        loadingMore.current = false;
      }
    },
    [
      scopeParams,
      pending.length,
      morePending.length,
      reviewItems,
      moreConfirmed.length,
      historyOffset,
    ],
  );

  const allAlerts = useMemo(
    () =>
      [...pendingItems, ...reviewMerged].filter((a) =>
        showOnAlertsPage(a, readAlertDisplayPrefs()),
      ),
    [pendingItems, reviewMerged, displayPrefs],
  );

  // Filter option lists come from the alerts actually present.
  const policyOptions = useMemo(() => {
    const keys = [...new Set(allAlerts.map(policyKey))].sort();
    return keys.map((k) => ({ value: k, label: k }));
  }, [allAlerts]);
  const cameraOptions = useMemo(() => {
    const ids = [...new Set(allAlerts.map((a) => a.camera_id))];
    return ids
      .map((id) => ({ value: id, label: cameraNameById.get(id) ?? id }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [allAlerts, cameraNameById]);

  const matches = useMemo(() => {
    const pol = new Set(selectedPolicies);
    const cam = new Set(selectedCameras);
    return (a: WeaponAlert) =>
      (pol.size === 0 || pol.has(policyKey(a))) &&
      (cam.size === 0 || cam.has(a.camera_id));
  }, [selectedPolicies, selectedCameras]);

  const filtersActive = selectedPolicies.length > 0 || selectedCameras.length > 0;

  const pendingShown = useMemo(
    () =>
      pendingItems.filter(
        (a) =>
          !hiddenIds.has(a.alert_id) &&
          showOnAlertsPage(a, readAlertDisplayPrefs()) &&
          matches(a),
      ),
    [pendingItems, displayPrefs, matches, hiddenIds],
  );
  const realAlarms = useMemo(
    () =>
      reviewMerged.filter(
        (a) =>
          !hiddenIds.has(a.alert_id) && a.status === "confirmed" && matches(a),
      ),
    [reviewMerged, matches, hiddenIds],
  );
  const falseAlarms = useMemo(
    () =>
      reviewMerged.filter(
        (a) =>
          !hiddenIds.has(a.alert_id) &&
          a.status === "cleared" &&
          showOnAlertsPage(a, readAlertDisplayPrefs()) &&
          matches(a),
      ),
    [reviewMerged, displayPrefs, matches, hiddenIds],
  );

  const showFalseColumn = readAlertDisplayPrefs().falsePositive === "alerts";
  const total = pendingShown.length + realAlarms.length + falseAlarms.length;

  const onDismiss = (a: WeaponAlert) =>
    dismissOne.mutate({ id: a.alert_id, fromHistory: a.status !== "pending" });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Alerts"
        icon={ShieldAlert}
        description={
          tab === "policies"
            ? "Set rules that watch a camera and alert you when something happens."
            : "Review alarms the system has raised."
        }
        actions={
          tab === "alerts" &&
          total > 0 && (
            <button
              type="button"
              onClick={() => {
                setHiddenIds((prev) => {
                  const next = new Set(prev);
                  for (const a of [...pendingShown, ...realAlarms, ...falseAlarms]) {
                    next.add(a.alert_id);
                  }
                  return next;
                });
              }}
              className="bf-button bg-white/10 bg-none"
            >
              Clear all
            </button>
          )
        }
      />

      {role.canManage && (
        <div
          role="tablist"
          aria-label="Alerts sections"
          className="flex w-fit items-center gap-1 rounded-lg border border-white/10 bg-bf-surface/60 p-1"
        >
          <button
            type="button"
            role="tab"
            aria-selected={tab === "alerts"}
            onClick={() => nav({ to: "/alerts", search: { tab: undefined }, replace: true })}
            className={
              "rounded-md px-3 py-1.5 text-sm font-medium transition " +
              (tab === "alerts"
                ? "bg-bf-violet/30 text-white"
                : "text-bf-muted hover:bg-white/5 hover:text-bf-text")
            }
          >
            Alerts
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === "policies"}
            onClick={() => nav({ to: "/alerts", search: { tab: "policies" }, replace: true })}
            className={
              "rounded-md px-3 py-1.5 text-sm font-medium transition " +
              (tab === "policies"
                ? "bg-bf-violet/30 text-white"
                : "text-bf-muted hover:bg-white/5 hover:text-bf-text")
            }
          >
            Policies
          </button>
        </div>
      )}

      {tab === "policies" ? (
        <PolicyAlerts embedded />
      ) : (
        <>
      {total > 0 && (
        <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-white/10 bg-ink-soft p-3">
          <span className="inline-flex items-center gap-1.5 text-sm font-medium text-bf-muted">
            <Filter size={15} /> Filter:
          </span>
          <MultiSelect
            className="w-52"
            ariaLabel="Filter by policy"
            allLabel="All policies"
            options={policyOptions}
            selected={selectedPolicies}
            onChange={setSelectedPolicies}
          />
          <MultiSelect
            className="w-52"
            ariaLabel="Filter by camera"
            allLabel="All cameras"
            options={cameraOptions}
            selected={selectedCameras}
            onChange={setSelectedCameras}
          />
          {filtersActive && (
            <button
              type="button"
              onClick={() => {
                setSelectedPolicies([]);
                setSelectedCameras([]);
              }}
              className="text-xs text-bf-muted transition hover:text-bf-text"
            >
              Reset filters
            </button>
          )}
        </div>
      )}

      {pendingShown.length > 0 && (
        <section>
          <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-amber-200">
            <AlertTriangle size={16} />
            Under verification
            <span className="rounded-full bg-amber-400/10 px-2 py-0.5 text-xs ring-1 ring-inset ring-amber-400/30">
              {filtersActive ? pendingShown.length : pendingTotal}
            </span>
          </div>
          <PendingAlertGrid
            items={pendingShown}
            totalCount={filtersActive ? pendingShown.length : pendingTotal}
            hasMore={!filtersActive && pendingShown.length < pendingTotal}
            onNeedMore={() => loadMore("pending")}
            cameraNameById={cameraNameById}
            storeNameFor={storeNameFor}
            onEnlarge={setEnlarged}
            onDismiss={onDismiss}
          />
        </section>
      )}

      {total === 0 ? (
        <div className="bf-card flex flex-col items-center gap-3 p-12 text-center">
          <ShieldCheck size={32} className="text-emerald-400" />
          <p className="text-bf-text">
            {scope.kind === "all" ? "No alerts" : `No alerts in ${scopeLabel}`}
          </p>
          <p className="max-w-md text-sm text-bf-muted">
            {displayPrefs.verification !== "alerts" || displayPrefs.falsePositive !== "alerts"
              ? "Verification or false alarms may be hidden — Settings → Alert settings."
              : "When the system raises an alarm it will appear here, sorted into real and false alarms."}
          </p>
        </div>
      ) : (
        <div className={showFalseColumn ? "grid gap-4 lg:grid-cols-2" : "grid gap-4"}>
          <AlertColumn
            title="Real alarms"
            icon={<ShieldAlert size={18} />}
            accentClass="text-brand-pink"
            items={realAlarms}
            totalCount={filtersActive ? realAlarms.length : confirmedTotal}
            hasMore={!filtersActive && realAlarms.length < confirmedTotal}
            onNeedMore={() => loadMore("confirmed")}
            cameraNameById={cameraNameById}
            storeNameFor={storeNameFor}
            emptyText="No confirmed alarms."
            onEnlarge={setEnlarged}
            onDismiss={onDismiss}
          />
          {showFalseColumn && (
            <AlertColumn
              title="False alarms"
              icon={<ShieldCheck size={18} />}
              accentClass="text-brand-violet"
              items={falseAlarms}
              hasMore={!filtersActive && historyMore}
              onNeedMore={() => loadMore("history")}
              cameraNameById={cameraNameById}
              storeNameFor={storeNameFor}
              emptyText="No false alarms."
              onEnlarge={setEnlarged}
              onDismiss={onDismiss}
            />
          )}
        </div>
      )}
        </>
      )}
    </div>
  );
}
